import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api/axios";
import {
  MapPin,
  Phone,
  Building2,
  Shield,
  Siren,
  Flame,
  Droplets,
  Pill,
  Loader as Loader2,
  ArrowLeft,
  Search,
  Navigation,
  X,
} from "lucide-react";
import BrandLogo from "../components/shared/BrandLogo";

const SERVICE_TYPES = [
  { key: "all", label: "All", icon: MapPin },
  { key: "hospital", label: "Hospital", icon: Building2 },
  { key: "ambulance", label: "Ambulance", icon: Siren },
  { key: "police", label: "Police", icon: Shield },
  { key: "fire_brigade", label: "Fire", icon: Flame },
  { key: "blood_bank", label: "Blood Bank", icon: Droplets },
  { key: "pharmacy", label: "Pharmacy", icon: Pill },
];

const TYPE_COLORS = {
  hospital: { bg: "bg-blue-50", border: "border-blue-200", text: "text-blue-700", icon: "text-blue-600" },
  ambulance: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", icon: "text-red-600" },
  police: { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", icon: "text-indigo-600" },
  fire_brigade: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", icon: "text-orange-600" },
  blood_bank: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", icon: "text-rose-600" },
  pharmacy: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", icon: "text-emerald-600" },
};

const TYPE_LABELS = {
  hospital: "Hospital",
  ambulance: "Ambulance",
  police: "Police",
  fire_brigade: "Fire Brigade",
  blood_bank: "Blood Bank",
  pharmacy: "Pharmacy",
};

const getTypeIcon = (type) => {
  const map = {
    hospital: Building2,
    ambulance: Siren,
    police: Shield,
    fire_brigade: Flame,
    blood_bank: Droplets,
    pharmacy: Pill,
  };
  return map[type] || MapPin;
};

const NearbyServicesPage = () => {
  const { locketId } = useParams();
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeType, setActiveType] = useState("all");
  const [mode, setMode] = useState("gps"); // "gps" | "pincode"
  const [pincode, setPincode] = useState("");
  const [searchRadius, setSearchRadius] = useState(null);
  const [locationGranted, setLocationGranted] = useState(false);
  const [callConfirm, setCallConfirm] = useState(null);

  const fetchByGPS = useCallback(async (lat, lng, type) => {
    setLoading(true);
    setError(null);
    try {
      const params = { lat, lng, radius: 10000, limit: 50 };
      if (type && type !== "all") params.type = type;
      const res = await api.get("/emergency-services/nearby", { params });
      setServices(res.data.services || []);
      setSearchRadius(res.data.searchRadius);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch nearby services");
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchByPincode = useCallback(async (code, type) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: 50 };
      if (type && type !== "all") params.type = type;
      const res = await api.get(`/emergency-services/by-pincode/${code}`, { params });
      setServices(res.data.services || []);
      setSearchRadius(null);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to fetch services for this pincode");
      setServices([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Store coords for re-fetching when type changes
  const [coords, setCoords] = useState(null);

  // Initial GPS fetch
  useEffect(() => {
    if (!navigator.geolocation) {
      setMode("pincode");
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoords({ lat: latitude, lng: longitude });
        setLocationGranted(true);
        fetchByGPS(latitude, longitude, "all");
      },
      () => {
        setMode("pincode");
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, [fetchByGPS]);

  // Re-fetch when type filter changes
  useEffect(() => {
    if (mode === "gps" && coords) {
      fetchByGPS(coords.lat, coords.lng, activeType);
    } else if (mode === "pincode" && pincode.length === 6) {
      fetchByPincode(pincode, activeType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeType]);

  const handlePincodeSubmit = (e) => {
    e.preventDefault();
    if (pincode.length !== 6) return;
    fetchByPincode(pincode, activeType);
  };

  const handleSwitchToGPS = () => {
    if (!navigator.geolocation) return;
    setMode("gps");
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setCoords({ lat: latitude, lng: longitude });
        setLocationGranted(true);
        fetchByGPS(latitude, longitude, activeType);
      },
      () => {
        setMode("pincode");
        setLoading(false);
        setError("Location permission denied. Please search by pincode.");
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const filtered = services;
  const backPath = locketId ? `/${locketId}/public` : "/";

  return (
    <div className="min-h-screen bg-slate-50 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-lg mx-auto px-3 py-2 flex items-center gap-3">
          <button
            onClick={() => navigate(backPath)}
            className="p-2 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors duration-150 shrink-0"
          >
            <ArrowLeft size={16} className="text-slate-700" />
          </button>
          <BrandLogo logoClassName="h-7" />
          <span className="text-sm font-bold text-slate-900 ml-auto">Nearby Services</span>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-3 pt-3">
        {/* Mode toggle + Pincode input */}
        <div className="mb-3">
          {mode === "pincode" || !locationGranted ? (
            <form onSubmit={handlePincodeSubmit} className="flex gap-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                  placeholder="Enter 6-digit pincode"
                  className="w-full pl-3 pr-3 py-2.5 border border-slate-300 rounded-lg text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={pincode.length !== 6}
                className="px-4 py-2.5 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white font-bold text-sm rounded-lg transition-colors duration-150 flex items-center gap-1.5"
              >
                <Search size={14} />
                Search
              </button>
              {navigator.geolocation && (
                <button
                  type="button"
                  onClick={handleSwitchToGPS}
                  className="px-3 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-sm rounded-lg transition-colors duration-150 flex items-center gap-1"
                  title="Use GPS location"
                >
                  <Navigation size={14} />
                </button>
              )}
            </form>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-sm text-emerald-700 font-medium bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 flex-1">
                <Navigation size={14} className="shrink-0" />
                Using your location
                {searchRadius && (
                  <span className="text-xs text-emerald-600 ml-1">({(searchRadius / 1000).toFixed(0)} km radius)</span>
                )}
              </div>
              <button
                onClick={() => { setMode("pincode"); setServices([]); setLoading(false); }}
                className="px-3 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-sm rounded-lg transition-colors duration-150 flex items-center gap-1"
              >
                <Search size={14} />
                Pincode
              </button>
            </div>
          )}
        </div>

        {/* Type filter tabs */}
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-3 scrollbar-hide">
          {SERVICE_TYPES.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveType(key)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-colors duration-150 border ${
                activeType === key
                  ? "bg-red-600 text-white border-red-600"
                  : "bg-white text-slate-700 border-slate-200 hover:border-red-300 hover:bg-red-50"
              }`}
            >
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <Loader2 size={32} className="text-red-600 animate-spin mb-3" />
            <p className="text-sm font-semibold text-slate-600">Finding nearby services...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg border-2 border-red-200 p-5 text-center">
            <MapPin size={32} className="text-red-400 mx-auto mb-2" />
            <p className="text-sm font-bold text-red-700 mb-1">{error}</p>
            <p className="text-xs text-slate-500">Try searching by pincode instead.</p>
          </div>
        ) : filtered.length === 0 && (mode === "gps" ? locationGranted : pincode.length === 6) ? (
          <div className="bg-white rounded-lg border border-slate-200 p-5 text-center">
            <MapPin size={32} className="text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-bold text-slate-700 mb-1">No services found</p>
            <p className="text-xs text-slate-500">
              {mode === "gps"
                ? "Try increasing the search area or switch to pincode search."
                : "Try a different pincode or switch to GPS."}
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-5 text-center">
            <Navigation size={32} className="text-slate-400 mx-auto mb-2" />
            <p className="text-sm font-bold text-slate-700 mb-1">
              {mode === "pincode" ? "Enter a pincode to search" : "Waiting for location..."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-slate-500 px-0.5">
              {filtered.length} service{filtered.length !== 1 ? "s" : ""} found
            </p>
            {filtered.map((svc, idx) => {
              const colors = TYPE_COLORS[svc.serviceType] || TYPE_COLORS.hospital;
              const TypeIcon = getTypeIcon(svc.serviceType);
              const phones = svc.phone || [];

              return (
                <div
                  key={idx}
                  className={`bg-white rounded-lg border ${colors.border} shadow-sm overflow-hidden`}
                >
                  {/* Card header */}
                  <div className="px-3 py-2.5 flex items-start gap-2.5">
                    <div className={`w-9 h-9 rounded-lg ${colors.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                      <TypeIcon size={18} className={colors.icon} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-slate-900 leading-tight">{svc.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] font-bold uppercase tracking-wide ${colors.text}`}>
                          {TYPE_LABELS[svc.serviceType] || svc.serviceType}
                        </span>
                        {svc.subType && (
                          <span className="text-[10px] text-slate-500 font-medium">
                            {svc.subType.replace(/_/g, " ")}
                          </span>
                        )}
                        {svc.isVerified && (
                          <span className="text-[10px] font-bold text-emerald-600">Verified</span>
                        )}
                      </div>
                      {svc.distanceKm != null && (
                        <p className="text-xs font-semibold text-slate-500 mt-0.5">
                          {svc.distanceKm < 1
                            ? `${Math.round(svc.distanceKm * 1000)} m away`
                            : `${svc.distanceKm} km away`}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Address */}
                  {svc.address && (
                    <div className="px-3 pb-2">
                      <p className="text-xs text-slate-600 leading-relaxed">
                        {svc.address}
                        {svc.city && `, ${svc.city}`}
                        {svc.pincode && ` - ${svc.pincode}`}
                      </p>
                    </div>
                  )}

                  {/* Metadata (open hours, specialties) */}
                  {svc.metadata && (
                    <div className="px-3 pb-2 flex flex-wrap gap-1">
                      {svc.metadata.openHours && (
                        <span className="text-[10px] font-medium text-slate-600 bg-slate-100 rounded px-1.5 py-0.5">
                          {svc.metadata.openHours}
                        </span>
                      )}
                      {svc.metadata.hasEmergencyWard && (
                        <span className="text-[10px] font-bold text-red-600 bg-red-50 rounded px-1.5 py-0.5">
                          Emergency Ward
                        </span>
                      )}
                      {svc.metadata.hasICU && (
                        <span className="text-[10px] font-bold text-blue-600 bg-blue-50 rounded px-1.5 py-0.5">
                          ICU
                        </span>
                      )}
                      {svc.metadata.tollFreeNumber && (
                        <a
                          href={`tel:${svc.metadata.tollFreeNumber}`}
                          className="text-[10px] font-bold text-emerald-700 bg-emerald-50 rounded px-1.5 py-0.5"
                        >
                          Toll-free: {svc.metadata.tollFreeNumber}
                        </a>
                      )}
                    </div>
                  )}

                  {/* Phone buttons */}
                  {phones.length > 0 && (
                    <div className="border-t border-slate-100 px-3 py-2 flex flex-wrap gap-1.5">
                      {phones.map((phone, pIdx) => (
                        <button
                          key={pIdx}
                          onClick={() => setCallConfirm({ name: svc.name, number: phone })}
                          className="flex items-center gap-1.5 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg px-2.5 py-1.5 transition-colors duration-150"
                        >
                          <Phone size={12} className="text-green-700" />
                          <span className="text-xs font-bold text-green-800 font-mono">{phone}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Directions link */}
                  {svc.location?.coordinates && (
                    <div className="border-t border-slate-100 px-3 py-2">
                      <a
                        href={`https://www.google.com/maps/dir/?api=1&destination=${svc.location.coordinates[1]},${svc.location.coordinates[0]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1.5 text-xs font-bold text-blue-700 hover:text-blue-800"
                      >
                        <Navigation size={12} />
                        Get Directions
                      </a>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Call confirmation modal */}
      {callConfirm && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4">
          <div className="bg-white w-full max-w-sm sm:rounded-lg overflow-hidden rounded-t-2xl">
            <div className="bg-red-600 px-4 py-3 flex items-center justify-between">
              <p className="text-white font-bold text-sm">Call {callConfirm.name}?</p>
              <button onClick={() => setCallConfirm(null)} className="text-white/80 hover:text-white">
                <X size={18} />
              </button>
            </div>
            <div className="px-4 py-4">
              <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-3 py-3">
                <div className="w-9 h-9 rounded-full bg-green-600 flex items-center justify-center shrink-0">
                  <Phone size={16} className="text-white" />
                </div>
                <p className="text-lg font-extrabold font-mono text-green-900">{callConfirm.number}</p>
              </div>
            </div>
            <div className="flex gap-2 px-4 pb-5">
              <button
                onClick={() => setCallConfirm(null)}
                className="flex-1 py-3 rounded-lg border-2 border-slate-200 bg-white text-slate-700 font-semibold text-sm hover:bg-slate-50 transition-colors duration-150"
              >
                Cancel
              </button>
              <a
                href={`tel:${callConfirm.number}`}
                onClick={() => setCallConfirm(null)}
                className="flex-1 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-bold text-sm text-center flex items-center justify-center gap-2 transition-colors duration-150"
              >
                <Phone size={16} />
                Call Now
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NearbyServicesPage;
